import React from "react";
import { Box, Typography } from "@mui/material";
import { PRIMARY_ENRICHMENT_PROMPTS } from "../../helper/suggestedEnrichmentPrompts";
import AiProviderIconStack from "./AiProviderIconStack";
import SuggestedEnrichmentPrompts from "./SuggestedEnrichmentPrompts";
import { WORKSPACE_ENRICH_CONTROL_HEIGHT as CONTROL_HEIGHT } from "./WorkspaceEnrichControls";

const MOTION_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

const MOTION_MS = "320ms";

const DESCRIPTION =
  "Use AI to enrich your selected companies with additional research and generated insights.";

function WorkspaceEnrichPanelView({
  isV3, cardSx, llmProvider, setLlmProvider,
  controlsDisabled, isDark, title, renderCombinedInput,
  selectedSuggestionId, handleSelectSuggestion, renderEnrichButton, titleCollision,
  creditUsageBanner, renderProgress
}) {
  return (
    <Box
      data-testid={isV3 ? "workspace-enrichment-panel-v3" : "workspace-enrichment-panel-v2"}

      sx={cardSx}
    >
      <Box
        data-enrich-layout=""
        sx={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(200px, 0.85fr) minmax(340px, 1.4fr)",
          },
          columnGap: { xs: 1.5, md: 2 },
          rowGap: 1.5,
          alignItems: "center",
          height: { xs: "auto", sm: CONTROL_HEIGHT },
          minHeight: { sm: CONTROL_HEIGHT },
          overflow: "visible",
          transition: [
            `column-gap ${MOTION_MS} ${MOTION_EASE}`,
            `row-gap ${MOTION_MS} ${MOTION_EASE}`,
          ].join(", "),
        }}
      >
        <Box
          data-enrich-identity=""
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1.25,
            minWidth: 0,
            flex: "0 0 auto",
            height: { xs: "auto", sm: CONTROL_HEIGHT },
            transition: [
              `gap ${MOTION_MS} ${MOTION_EASE}`,
              `max-width ${MOTION_MS} ${MOTION_EASE}`,
            ].join(", "),
          }}
        >
          <AiProviderIconStack
            selectedProviderId={llmProvider}
            onSelectProvider={setLlmProvider}
            disabled={controlsDisabled}
          />

          <Box sx={{ minWidth: 0, position: "relative", display: "flex", flexDirection: "column", gap: 0 }}>
            <Typography
              data-enrich-title=""
              variant="subtitle1"
              sx={{
                m: 0,
                fontWeight: 700,
                fontSize: 14,
                lineHeight: 1.2,
                letterSpacing: "-0.02em",
                color: isDark ? "#FFFFFF" : "#171717",
                whiteSpace: "nowrap",
                transition: `font-size ${MOTION_MS} ${MOTION_EASE}`,
              }}
            >
              {title}
            </Typography>

            <Typography
              data-enrich-description=""
              variant="body2"
              sx={{
                m: 0,
                maxWidth: 280,
                fontSize: 12,
                fontWeight: 400,
                lineHeight: 1.45,
                color: isDark ? "rgba(255,255,255,0.62)" : "#6B6C70",
                position: "absolute",
                visibility: "hidden",
                height: 0,
                minHeight: 0,
                opacity: 0,
                maxHeight: 0,
                overflow: "hidden",
                marginTop: 0,
                transform: "translateY(-4px)",
                pointerEvents: "none",
                transition: [
                  `opacity ${MOTION_MS} ${MOTION_EASE}`,
                  `max-height ${MOTION_MS} ${MOTION_EASE}`,
                  `margin-top ${MOTION_MS} ${MOTION_EASE}`,
                  `transform ${MOTION_MS} ${MOTION_EASE}`,
                ].join(", "),
              }}
            >
              {DESCRIPTION}
            </Typography>
          </Box>
        </Box>

        <Box
          data-enrich-controls=""
          sx={{
            display: "grid",
            gridTemplateColumns: { sm: "minmax(0, 1fr) auto" },
            gridTemplateAreas: { sm: `"input cta" "prompts prompts"` },
            gridTemplateRows: { sm: `${CONTROL_HEIGHT}px 0px` },
            columnGap: 1.25,
            rowGap: { sm: 0 },
            alignItems: "stretch",
            alignSelf: { xs: "stretch", md: "center" },
            minWidth: 0,
            width: "100%",
            transition: `grid-template-rows ${MOTION_MS} ${MOTION_EASE}`,
          }}
        >
          {renderCombinedInput({ sx: { gridArea: "input", height: "100%" } })}

          <Box
            data-enrich-prompts=""
            data-testid="enrich-prompts-slot"
            sx={{
              gridArea: "prompts",
              width: "100%",
              minHeight: 0,
              height: 0,
              overflow: "hidden",
              opacity: 0,
              maxHeight: 0,
              marginTop: 0,
              paddingTop: 0,
              transform: "none",
              pointerEvents: "none",
              transition: [
                `opacity ${MOTION_MS} ${MOTION_EASE}`,
                `max-height ${MOTION_MS} ${MOTION_EASE}`,
                `margin-top ${MOTION_MS} ${MOTION_EASE}`,
                `padding-top ${MOTION_MS} ${MOTION_EASE}`,
              ].join(", "),
            }}
          >
            <SuggestedEnrichmentPrompts
              prompts={PRIMARY_ENRICHMENT_PROMPTS}
              selectedId={selectedSuggestionId}
              onSelect={handleSelectSuggestion}
              disabled={controlsDisabled}
              align="start"
            />
          </Box>

          {renderEnrichButton({ sx: { gridArea: "cta", height: "100%" } })}
        </Box>

        {titleCollision ? <Typography role="alert" variant="caption" color="error" sx={{ mt: 0.75 }}>
          “{titleCollision}” is already used. Pick another column title.
        </Typography> : null}
      </Box>

      {creditUsageBanner}
      {renderProgress()}
    </Box>
  );
}

export { WorkspaceEnrichPanelView, MOTION_EASE, MOTION_MS, DESCRIPTION };
